import {
    WIDGET_SHADOW_ROOT_ENABLED_DATA_ATTR,
    WIDGET_VERSION,
} from "@/constants";
import { REVIEWS_WIDGET_NAMES } from "@/constants/widget-names";
import { globalProductIdManagerToken } from "@/tokens";
import {
    ListingAverageReviewsWidgetDefinition,
    ListingAverageReviewsWidgetDefinitionV2,
} from "@apps/reviews/definitions";
import CustomizationManager from "@lib/CustomizationManager/CustomizationManager";
import { ProductIdFromStaticProvider } from "@lib/ProductIdManager/ProductIdFromStaticProvider";
import { ProductIdManager } from "@lib/ProductIdManager/ProductIdManager";
import AnalyticsEventsCollectorInterface from "@modules/analytics_events_collector/AnalyticsEventsCollector/AnalyticsEventsCollectorInterface";
import { AnalyticsEventsFactory } from "@modules/analytics_events_collector/AnalyticsEventsCollector/AnalyticsEventsFactory";
import { DynamicImportManager } from "@modules/dynamic_import/DynamicImportManager";
import WidgetInjectedSdkEvent from "@modules/sdk/common_sdk/events/WidgetInjectedSdkEvent";
import { dispatchSdkEvent } from "@modules/sdk/helpers";
import MutationObserverInjector from "@ui-utils/Injector/MutationObserverInjector";
import {
    parseIntDataAttr,
    parseIntFromGidDataAttr,
    parseOptionalBooleanDataAttr,
} from "@ui-utils/Injector/utils";
import { container } from "tsyringe";

import { LoggerInterface } from "@interfaces/LoggerInterface";

const OLD_REVIEW_PRODUCT_ID = "product_id";
const REVIEW_PRODUCT_ID = "gw-product-id";
const SHOW_REVIEWS_TOOLTIP = "gw-show-reviews-tooltip";

class ListingAverageReviewsWidgetInjector {
    constructor(
        private readonly placeholderSelector: string,
        private readonly logger: LoggerInterface,
        private readonly eventsCollector: AnalyticsEventsCollectorInterface,
        private readonly analyticsEventsFactory: AnalyticsEventsFactory,
        private readonly dynamicImportManager: DynamicImportManager,
        private readonly moi: MutationObserverInjector
    ) {}

    start(): void {
        this.logger.debug(
            "ListingAverageReviewsWidgetInjector start",
            this.placeholderSelector
        );

        const targets$ = this.moi.register({
            targetSelector: this.placeholderSelector,
        });

        targets$.subscribe((placeholder) => {
            this.logger?.debug(
                "ListingAverageReviewsWidgetInjector found placeholder",
                {
                    placeholder,
                }
            );

            let productIdFromDataAttr =
                parseIntDataAttr(placeholder, REVIEW_PRODUCT_ID) ||
                parseIntFromGidDataAttr(placeholder, REVIEW_PRODUCT_ID);
            if (productIdFromDataAttr === null) {
                productIdFromDataAttr = parseIntDataAttr(
                    placeholder,
                    OLD_REVIEW_PRODUCT_ID
                );

                if (productIdFromDataAttr !== null) {
                    this.eventsCollector.pushEvent(
                        this.analyticsEventsFactory.createWidgetInjectedWithAttribute(
                            REVIEWS_WIDGET_NAMES.LISTING_AVERAGE_REVIEWS_WIDGET,
                            OLD_REVIEW_PRODUCT_ID
                        )
                    );
                }
            }

            if (productIdFromDataAttr === null) {
                this.logger.error(
                    `ListingAverageReviewsWidget placeholder does not have data-${REVIEW_PRODUCT_ID} attribute.`
                );
            }

            const productIdManager =
                productIdFromDataAttr === null
                    ? container.resolve(globalProductIdManagerToken)
                    : new ProductIdManager();

            if (productIdFromDataAttr !== null) {
                productIdManager.registerProvider(
                    new ProductIdFromStaticProvider(productIdFromDataAttr)
                );
            }

            void (async () => {
                const customizationManager =
                    container.resolve(CustomizationManager);
                const widgetVersion = customizationManager.defineWidgetVersion({
                    widgetName:
                        REVIEWS_WIDGET_NAMES.LISTING_AVERAGE_REVIEWS_WIDGET_V2,
                    placeholder,
                });

                if (widgetVersion === WIDGET_VERSION.V1) {
                    const ListingAverageReviewsWidget =
                        await this.dynamicImportManager.importDefault(
                            ListingAverageReviewsWidgetDefinition,
                            true
                        );

                    const widget = new ListingAverageReviewsWidget(
                        {
                            target: placeholder,
                            isWidgetShadowRootEnabled:
                                parseOptionalBooleanDataAttr(
                                    placeholder,
                                    WIDGET_SHADOW_ROOT_ENABLED_DATA_ATTR
                                ),
                            showReviewsTooltip:
                                parseOptionalBooleanDataAttr(
                                    placeholder,
                                    SHOW_REVIEWS_TOOLTIP
                                ) || false,
                        },
                        this.logger,
                        productIdManager
                    );

                    await widget.init();

                    this.eventsCollector.pushEvent(
                        this.analyticsEventsFactory.createWidgetInjected(
                            REVIEWS_WIDGET_NAMES.LISTING_AVERAGE_REVIEWS_WIDGET
                        )
                    );

                    dispatchSdkEvent(
                        new WidgetInjectedSdkEvent(
                            REVIEWS_WIDGET_NAMES.LISTING_AVERAGE_REVIEWS_WIDGET
                        )
                    );
                } else {
                    const ListingAverageReviewsWidgetV2 =
                        await this.dynamicImportManager.importDefault(
                            ListingAverageReviewsWidgetDefinitionV2,
                            true
                        );

                    const widget = new ListingAverageReviewsWidgetV2(
                        {
                            target: placeholder,
                            isWidgetShadowRootEnabled:
                                parseOptionalBooleanDataAttr(
                                    placeholder,
                                    WIDGET_SHADOW_ROOT_ENABLED_DATA_ATTR
                                ),
                            showReviewsTooltip:
                                parseOptionalBooleanDataAttr(
                                    placeholder,
                                    SHOW_REVIEWS_TOOLTIP
                                ) || false,
                        },
                        this.logger,
                        productIdManager
                    );

                    await widget.init();

                    this.eventsCollector.pushEvent(
                        this.analyticsEventsFactory.createWidgetInjected(
                            REVIEWS_WIDGET_NAMES.LISTING_AVERAGE_REVIEWS_WIDGET_V2
                        )
                    );

                    dispatchSdkEvent(
                        new WidgetInjectedSdkEvent(
                            REVIEWS_WIDGET_NAMES.LISTING_AVERAGE_REVIEWS_WIDGET_V2
                        )
                    );
                }
            })();
        });
    }
}

export default ListingAverageReviewsWidgetInjector;
